/**
 * Book Setting Service
 * Fetches document/book settings (ruler unit, trim size, margins) from the Larry backend.
 * Larry backend proxies to Load Service → Planbook (GET /bookSetting/all?jobId=...).
 * Job ID is read from the authenticated user's JWT claims.
 */

import { getJobIdFromStoredJwt } from '@/api/services/auth.service'
import { httpClient } from '@/api/http/client'
import { logger } from '@/utils/logger'

export type RulerUnit = 'pica' | 'point' | 'inch'

/** API unitOfMeasure values (payload.unitOfMeasure) */
export type UnitOfMeasureValue = 'Picas' | 'Points' | 'Inches'

export interface BookTrimSize {
  trimSizeName: string
  trimSizeId: string
  width: number
  height: number
}

export interface BookJob {
  pageCount: number
  trimSizeId?: string
  pageEditorTypeId?: number
  jobId?: string
  jobNumber?: string
  defaultFontFamilyId?: number
  defaultFontSize?: number
  [key: string]: unknown
}

export interface BookSettingPayload {
  unitOfMeasure: string
  snapToGuide?: boolean
  insideMargin?: number
  jobId?: string
  topMargin?: number
  linkedGutterValues?: boolean
  bookTrimSize?: BookTrimSize
  bottomMargin?: number
  setDefaultColumnRow?: boolean
  job?: BookJob
  outsideMargin?: number
  /** API-only fields */
  columnNum?: number
  rowNum?: number
  columnGutter?: number
  rowGutter?: number
  gridUnits?: string
  gridShowHide?: boolean
  snapToGrid?: boolean
  /** Last update time for planbook-level editor settings. */
  planbookUpdatedAt?: string
}

export interface BookSettingAllResponse {
  result: boolean
  payload: BookSettingPayload
}

/** Document grid overlay mode in the editor (matches `home` / `CenterCanvas`). */
export type EditorGridMode = 'off' | 'full' | 'half'

/**
 * Maps Planbook `gridUnits` ("Full Picas" | "Half Picas") to editor spacing.
 * Unknown or missing values default to full-pica spacing.
 */
export function gridUnitsToEditorGridSpacing(gridUnits: string | undefined): 'full' | 'half' {
  const n = gridUnits?.trim().toLowerCase() ?? ''

  if (n === 'half picas') return 'half'
  return 'full'
}

/**
 * Derives editor grid visibility/spacing from book settings payload.
 * - `gridShowHide === false` → grid hidden (`off`).
 * - `gridShowHide === true` → shown; full vs half from `gridUnits` (default full).
 * - If `gridShowHide` is omitted but `gridUnits` is set and the grid was visible, updates full/half only.
 */
export function editorGridModeFromBookPayload(
  payload: Pick<BookSettingPayload, 'gridShowHide' | 'gridUnits'>,
  prev: EditorGridMode
): EditorGridMode {
  if (payload.gridShowHide === false) return 'off'
  if (payload.gridShowHide === true) {
    return gridUnitsToEditorGridSpacing(payload.gridUnits)
  }

  if (payload.gridUnits != null && String(payload.gridUnits).trim() !== '') {
    if (prev !== 'off') {
      return gridUnitsToEditorGridSpacing(payload.gridUnits)
    }
  }

  return prev
}

/**
 * Snap-to-grid from book payload.
 * - `gridShowHide === true` always forces snap on (grid visible implies snap-to-grid).
 * - Otherwise, explicit `snapToGrid` wins; if omitted but `gridShowHide` is a boolean, snap
 *   matches show/hide.
 */
export function snapToGridFromBookPayload(
  payload: Pick<BookSettingPayload, 'gridShowHide' | 'snapToGrid'>,
  prev: boolean
): boolean {
  if (payload.gridShowHide === true) return true
  if (typeof payload.snapToGrid === 'boolean') return payload.snapToGrid
  if (typeof payload.gridShowHide === 'boolean') return payload.gridShowHide
  return prev
}

/**
 * Snap-to-guides from book payload. Explicit `snapToGuide` wins; otherwise keeps previous.
 */
export function snapToGuidesFromBookPayload(
  payload: Pick<BookSettingPayload, 'snapToGuide'>,
  prev: boolean
): boolean {
  if (typeof payload.snapToGuide === 'boolean') return payload.snapToGuide
  return prev
}

/**
 * Maps API unitOfMeasure string to ruler unit type.
 * Case-insensitive; unknown or missing values fall back to 'pica'.
 * API returns "Picas" | "Points" | "Inches".
 */
export function unitOfMeasureToRulerUnit(value: string | undefined | null): RulerUnit {
  const normalized = (value ?? '').trim().toLowerCase()

  if (normalized === 'picas') return 'pica'
  if (normalized === 'points') return 'point'
  if (normalized === 'inches') return 'inch'
  return 'pica'
}

/**
 * Supported book sizes (per-page dimensions in points; 72 pt = 1 inch).
 * Size 7: 7 3/4" x 10 1/2". Size 8: 8 1/2" x 11". Size 9: 9" x 12".
 */
export const SUPPORTED_BOOK_TRIM_SIZES: Record<
  '07' | '08' | '09',
  { trimSizeName: string; trimSizeId: string; width: number; height: number }
> = {
  '07': {
    trimSizeName: '7 3/4" x 10 1/2"',
    trimSizeId: '07',
    width: 7.75 * 72, // 558
    height: 10.5 * 72, // 756
  },
  '08': {
    trimSizeName: '8 1/2" x 11"',
    trimSizeId: '08',
    width: 8.5 * 72, // 612
    height: 11 * 72, // 792
  },
  '09': {
    trimSizeName: '9" x 12"',
    trimSizeId: '09',
    width: 9 * 72, // 648
    height: 12 * 72, // 864
  },
}

/**
 * Get book settings from the Larry backend (GET /booksettings/:jobId).
 * Job ID is read from the authenticated user's JWT; auth uses the shared httpClient (Bearer JWT).
 */
export async function getBookSettingAll(): Promise<BookSettingAllResponse> {
  const jobId = getJobIdFromStoredJwt()

  if (!jobId) {
    throw new Error('Book settings require authentication (no job ID in token). Please log in.')
  }

  const path = `/booksettings/${encodeURIComponent(jobId)}`

  logger.debug('Book settings request', { path, jobId })

  const data = await httpClient.get<BookSettingAllResponse>(path)

  if (!data?.payload) {
    throw new Error('Book settings API returned invalid response (missing payload)')
  }

  return data
}
